import { useCallback } from 'react';
import moment from 'moment-timezone';
import type {
  CalendarComponentEvent,
  CalendarPermissions,
} from '@axiom/validation';

import { CalendarEventsUtil } from './calendar-events-util';
import {
  EDIT_MODE_LEAD_TIME_MINUTES,
  EVENT_STATES,
} from './calendar-events.const';

type UseCalendarEventStylingParams = {
  editableEventId?: string | null;
  perms: CalendarPermissions;
  timezone?: string;
};

export const useCalendarEventStyling = ({
  editableEventId,
  perms,
  timezone,
}: UseCalendarEventStylingParams) => {
  const eventPropGetter = useCallback(
    (event: CalendarComponentEvent) => {
      const data = {
        className: 'nonDraggable',
      };

      if (editableEventId != null) {
        const isDraggable = event.id === editableEventId;
        const draggableClass = isDraggable ? 'isDraggable' : 'nonDraggable';
        // While editing a single event, fade every other event so users
        // can see at a glance which one is in scope. Behaviorally the
        // others are already locked via the nonDraggable class — this
        // just adds the visual cue.
        const editLockClass = isDraggable ? '' : ' editLocked';
        data.className = event.busy
          ? `staticEvent ${draggableClass}${editLockClass}`
          : `${draggableClass}${editLockClass}`;
      } else if (event.busy) {
        data.className = `staticEvent ${
          perms.staticEvent.canMove ? 'isDraggable' : 'nonDraggable'
        }`;
      } else {
        data.className = perms.eventBoundary.canMove
          ? 'isDraggable'
          : 'nonDraggable';
      }

      const isInterview = event.busy;
      const isActive =
        event.state !== EVENT_STATES.SAVED || event.id === editableEventId;
      if (isInterview && isActive) {
        data.className += ' activeInterview';
      }

      if (event.error) {
        data.className += ' error';
      }

      return data;
    },
    [editableEventId, perms]
  );

  const slotPropGetter = useCallback(
    (date: Date) => {
      // In edit mode, the gray "inactive" overlay should match the actual
      // schedulable boundary — the same `now + LEAD_TIME` cutoff enforced
      // by `setEventError`'s past-start check. Bucket-equality from
      // `isInThePast` would gray out the entire current clock-hour, hiding
      // valid drop targets like 10:35 when now is 10:30. In non-edit modes
      // (creating new events, viewing) the bucket logic encodes the
      // canSameDay lead-time gate and stays in place.
      const isUnschedulable =
        editableEventId == null
          ? CalendarEventsUtil.isInThePast(date, perms, timezone)
          : moment(date).isBefore(
              moment().add(EDIT_MODE_LEAD_TIME_MINUTES, 'minutes')
            );

      return { className: isUnschedulable ? 'inactive-time-block' : '' };
    },
    [editableEventId, perms, timezone]
  );

  return {
    eventPropGetter,
    slotPropGetter,
  };
};
